/**
 * LRU cache for SalaryLab calculation results.
 * Stores results in memory per page load so toggling between
 * previously-calculated values returns instantly without a fetch.
 */
export class ResultCache {
  /**
   * @param {number} maxSize - Maximum entries before LRU eviction
   */
  constructor(maxSize = 50) {
    this._maxSize = maxSize
    this._map = new Map() // Map preserves insertion order; we re-insert on access for LRU
  }

  /**
   * Build a stable cache key from a params object.
   * Sorts keys to ensure {a:1, b:2} and {b:2, a:1} produce the same key.
   * @param {Object} params
   * @returns {string}
   */
  _key(params) {
    const sortedKeys = Object.keys(params).sort()
    const stable = {}
    for (const k of sortedKeys) {
      stable[k] = params[k]
    }
    return JSON.stringify(stable)
  }

  /**
   * Retrieve a cached result. Returns undefined on miss.
   * Promotes the entry to most-recently-used on hit.
   * @param {Object} params
   * @returns {Object|undefined}
   */
  get(params) {
    const key = this._key(params)
    if (!this._map.has(key)) return undefined

    const value = this._map.get(key)
    // Promote to most-recently-used by re-inserting
    this._map.delete(key)
    this._map.set(key, value)
    return value
  }

  /**
   * Store a result. Evicts the least-recently-used entry if full.
   * @param {Object} params
   * @param {Object} result
   */
  set(params, result) {
    const key = this._key(params)

    // If key exists, delete first so re-insert moves it to end (most recent)
    if (this._map.has(key)) {
      this._map.delete(key)
    }

    this._map.set(key, result)

    // Evict oldest (first inserted) if over capacity
    if (this._map.size > this._maxSize) {
      const oldest = this._map.keys().next().value
      this._map.delete(oldest)
    }
  }

  /** Current number of cached entries. */
  get size() {
    return this._map.size
  }

  /** Clear all cached entries. */
  clear() {
    this._map.clear()
  }
}
